import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, Input, Button, message, Spin } from "antd";
import CurrencyFormatter from "../../../../components/CurrencyFormatter";

import {
  bindDevice,
  getDeviceStatus,
  refreshDeviceToken,
  getBranchBasicInfo,
} from "../../../../network/network";

import { generateFingerprint } from "../../../../utils/device/fingerprint";
import {
  clearDeviceAuth,
  saveDeviceAuth,
  getDeviceToken,
  getDeviceAuth,
} from "../../../../utils/device/deviceAuth";

import { getEcho, resetEcho } from "../../../../bootstrap/echo";
import { setEchoAuthToken } from "../../../../bootstrap/echoAuth";
import { useTranslation } from "react-i18next";
import {
  handleApiError,
  showAppError,
} from "../../../../utils/errorHandler";
import { showWarningMessage } from "../../../../utils/antdMessage";
import { DeviceType } from "../../../../configs/constants";

const DEVICE_TYPE = DeviceType.Cds;

const emptyUiData = {
  status: "IDLE",
  items: [],
  totalTax: 0,
  totalAmount: 0,
  shop: { business_name: "", logo: "" },
  cashier_name: "",
};

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }

  return 0;
};

const customerDisplayTheme = {
  primary: "var(--color-primary)",
  primaryDark: "var(--color-primary-dark, var(--color-heading))",
  primarySoft: "var(--color-primary-soft)",
  heading: "var(--color-heading)",
  text: "var(--color-text)",
  textMuted: "var(--color-text-light, var(--color-text))",
  bg: "var(--color-bg)",
  white: "var(--color-white)",
  border: "var(--color-border)",
  shadow: "var(--color-shadow)",
  shadowSoft: "var(--color-shadow-soft)",
  success: "var(--color-success, var(--color-primary))",
  warning: "var(--color-warning, var(--color-primary))",
};

const customerDisplayStyles = {
  page: {
    minHeight: "calc(100vh - 36px)",
    height: "calc(100vh - 36px)",
    display: "flex",
    gap: 18,
    padding: 18,
    overflow: "hidden",
    background: customerDisplayTheme.bg,
    color: customerDisplayTheme.heading,
  },
  itemsPanel: {
    width: "58%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    padding: 20,
    borderRadius: 24,
    background: customerDisplayTheme.white,
    border: `1px solid ${customerDisplayTheme.border}`,
    boxShadow: `0 18px 44px ${customerDisplayTheme.shadowSoft}`,
  },
  itemsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 16,
    paddingBottom: 14,
    borderBottom: `1px solid ${customerDisplayTheme.border}`,
  },
  eyebrow: {
    color: customerDisplayTheme.textMuted,
    fontSize: 13,
    fontWeight: 700,
  },
  panelTitle: {
    color: customerDisplayTheme.heading,
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 13px",
    borderRadius: 999,
    color: customerDisplayTheme.primaryDark,
    background: customerDisplayTheme.primarySoft,
    border: `1px solid ${customerDisplayTheme.border}`,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  statusDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    boxShadow: `0 0 0 5px ${customerDisplayTheme.primarySoft}`,
  },
  statusDotActive: {
    background: customerDisplayTheme.success,
  },
  statusDotIdle: {
    background: customerDisplayTheme.warning,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingInlineEnd: 8,
  },
  itemCard: {
    padding: 16,
    marginBottom: 12,
    borderRadius: 18,
    background: customerDisplayTheme.white,
    border: `1px solid ${customerDisplayTheme.border}`,
    boxShadow: `0 10px 24px ${customerDisplayTheme.shadowSoft}`,
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productNameAr: {
    color: customerDisplayTheme.heading,
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.25,
  },
  productNameEn: {
    color: customerDisplayTheme.text,
    fontWeight: 600,
    fontSize: 14,
    marginTop: 5,
  },
  qtyLine: {
    color: customerDisplayTheme.text,
    fontSize: 14,
    fontWeight: 600,
    marginTop: 10,
  },
  itemPrice: {
    minWidth: 152,
    textAlign: "start",
    color: customerDisplayTheme.heading,
    fontWeight: 800,
    fontSize: 21,
  },
  optionsLabel: {
    color: customerDisplayTheme.textMuted,
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 7,
  },
  tagWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  tagPill: {
    background: customerDisplayTheme.primarySoft,
    border: `1px solid ${customerDisplayTheme.border}`,
    color: customerDisplayTheme.primaryDark,
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },
  emptyState: {
    height: "100%",
    minHeight: 360,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: customerDisplayTheme.heading,
    borderRadius: 22,
    border: `1px dashed ${customerDisplayTheme.border}`,
    background: customerDisplayTheme.bg,
  },
  emptyIcon: {
    width: 82,
    height: 82,
    borderRadius: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
    color: customerDisplayTheme.primaryDark,
    background: customerDisplayTheme.primary,
    fontSize: 38,
    fontWeight: 800,
    boxShadow: `0 14px 28px ${customerDisplayTheme.shadowSoft}`,
  },
  emptyTitle: {
    fontSize: 28,
    fontWeight: 800,
  },
  emptySubTitle: {
    color: customerDisplayTheme.text,
    fontSize: 16,
    marginTop: 8,
    fontWeight: 600,
  },
  summaryPanel: {
    width: "42%",
    minWidth: 0,
    color: customerDisplayTheme.white,
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 24,
    borderRadius: 24,
    background: customerDisplayTheme.heading,
    boxShadow: `0 20px 52px ${customerDisplayTheme.shadow}`,
    overflow: "hidden",
    position: "relative",
  },
  brandCard: {
    display: "flex",
    alignItems: "center",
    gap: 15,
    padding: 18,
    borderRadius: 20,
    background: customerDisplayTheme.white,
    border: `1px solid ${customerDisplayTheme.border}`,
    color: customerDisplayTheme.heading,
  },
  logo: {
    width: 72,
    height: 72,
    borderRadius: 18,
    objectFit: "cover",
    background: customerDisplayTheme.white,
    border: `1px solid ${customerDisplayTheme.border}`,
  },
  logoFallback: {
    width: 72,
    height: 72,
    borderRadius: 18,
    background: customerDisplayTheme.primary,
    color: customerDisplayTheme.primaryDark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    letterSpacing: 1,
    border: `1px solid ${customerDisplayTheme.border}`,
  },
  brandInfo: {
    flex: 1,
    minWidth: 0,
  },
  storeName: {
    fontSize: 25,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  cashier: {
    marginTop: 9,
    fontSize: 15,
    fontWeight: 700,
    color: customerDisplayTheme.text,
  },
  review: {
    marginTop: 8,
    color: customerDisplayTheme.textMuted,
    fontSize: 13,
    fontWeight: 600,
  },
  totalCard: {
    flex: 1,
    minHeight: 260,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: 28,
    borderRadius: 24,
    background: customerDisplayTheme.white,
    color: customerDisplayTheme.heading,
    boxShadow: `0 16px 36px ${customerDisplayTheme.shadowSoft}`,
  },
  totalLabel: {
    color: customerDisplayTheme.text,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  totalValue: {
    marginTop: 12,
    color: customerDisplayTheme.primaryDark,
    fontSize: "clamp(46px, 5vw, 72px)",
    fontWeight: 800,
    lineHeight: 1.05,
  },
  taxBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 24,
    padding: "14px 16px",
    borderRadius: 16,
    background: customerDisplayTheme.primarySoft,
    border: `1px solid ${customerDisplayTheme.border}`,
    color: customerDisplayTheme.heading,
    fontSize: 16,
    fontWeight: 700,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    color: customerDisplayTheme.white,
    fontSize: 13,
    fontWeight: 700,
  },
  powered: {
    color: customerDisplayTheme.primaryDark,
    background: customerDisplayTheme.primary,
    padding: "9px 13px",
    borderRadius: 999,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  pairingPage: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
    backgroundImage:
      "linear-gradient(var(--color-overlay-dark, rgba(0, 3, 25, 0.62)), var(--color-overlay-dark, rgba(0, 3, 25, 0.62))), url('/images/customer-bg-img.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  pairingCard: {
    width: 430,
    borderRadius: 22,
    border: `1px solid ${customerDisplayTheme.border}`,
    background: customerDisplayTheme.white,
    boxShadow: `0 24px 70px ${customerDisplayTheme.shadow}`,
    overflow: "hidden",
  },
  pairingHeader: {
    textAlign: "center",
    marginTop: 18,
  },
  pairingIcon: {
    width: 68,
    height: 68,
    margin: "0 auto 14px",
    borderRadius: 20,
    background: customerDisplayTheme.primary,
    color: customerDisplayTheme.primaryDark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    fontWeight: 800,
  },
  pairingTitle: {
    fontSize: 21,
    fontWeight: 800,
    color: customerDisplayTheme.heading,
  },
  pairingSubTitle: {
    fontSize: 15,
    fontWeight: 700,
    direction: "rtl",
    color: customerDisplayTheme.text,
    marginTop: 4,
  },
  pairingInput: {
    marginBottom: 12,
    borderRadius: 12,
    height: 46,
    fontSize: 18,
    letterSpacing: 4,
    textAlign: "center",
  },
  pairingButton: {
    height: 46,
    borderRadius: 12,
    fontWeight: 800,
  },
  loadingPage: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: customerDisplayTheme.bg,
  },
};

const CustomerExternalDisplay = () => {
  const bottomRef = useRef(null);
  const { t } = useTranslation();

  const [checkingDevice, setCheckingDevice] = useState(true);
  const [paired, setPaired] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [pairingLoading, setPairingLoading] = useState(false);

  const [posDeviceUid, setPosDeviceUid] = useState(null);
  const [data, setData] = useState(emptyUiData);

  // Load branch basic info (logo + store name)
  useEffect(() => {
    if (!paired) return;

    let isEffectActive = true;

    (async () => {
      try {
        const res = await getBranchBasicInfo(DEVICE_TYPE);

        if (res?.data?.success === false) {
          if (isEffectActive) handleApiError(res, t);
          return;
        }

        const raw = res?.data?.data || {};
        const company = raw?.company || {};

        const company_name = company?.name || "";
        const company_logo = company?.logo || "";

        if (!isEffectActive) return;

        setData((prev) => ({
          ...prev,
          shop: {
            ...prev.shop,
            business_name: company_name || prev.shop?.business_name || "",
            logo: company_logo || prev.shop?.logo || "",
          },
        }));
      } catch (error) {
        if (isEffectActive) handleApiError(error, t);
      }
    })();

    return () => {
      isEffectActive = false;
    };
  }, [paired, t]);

  const subscribedChannelRef = useRef(null);

  const onUnauthorized = useCallback(() => {
    try {
      const prev = subscribedChannelRef.current;
      if (prev) window.Echo?.leave?.(prev);
    } catch {}
    subscribedChannelRef.current = null;

    try {
      resetEcho();
    } catch {}

    clearDeviceAuth(DEVICE_TYPE);
    setPaired(false);
    setPairCode("");
    setPosDeviceUid(null);
    setData(emptyUiData);

    showWarningMessage("Device unpaired • تم إلغاء ربط الجهاز");
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        const prev = subscribedChannelRef.current;
        if (prev) window.Echo?.leave?.(prev);
      } catch {}
      subscribedChannelRef.current = null;

      try {
        resetEcho();
      } catch {}
    };
  }, []);

  useEffect(() => {
    let isEffectActive = true;

    const verify = async () => {
      try {
        const token = getDeviceToken(DEVICE_TYPE);

        if (!token) {
          if (isEffectActive) {
            setPaired(false);
            setCheckingDevice(false);
          }
          return;
        }

        const statusRes = await getDeviceStatus(DEVICE_TYPE);
        if (!isEffectActive) return;

        const ok =
          statusRes?.data?.success === true &&
          statusRes?.data?.data?.is_active !== false;

        const posUid = statusRes?.data?.data?.linked_pos_device_uid || null;

        if (ok && posUid) {
          if (isEffectActive) {
            setPaired(true);
            setPosDeviceUid(posUid);
            setCheckingDevice(false);
          }
          return;
        }

        throw new Error(!ok ? "Device status invalid" : "Missing POS link");
      } catch {
        if (!isEffectActive) return;

        try {
          const fingerprint = await generateFingerprint();
          if (!isEffectActive) return;

          const refreshRes = await refreshDeviceToken(
            { device_fingerprint: fingerprint },
            DEVICE_TYPE,
          );
          if (!isEffectActive) return;

          const refreshOk = refreshRes?.data?.success === true;
          const auth = refreshRes?.data?.data;

          if (!refreshOk || !auth?.token) {
            if (isEffectActive) {
              handleApiError(refreshRes, t);
              onUnauthorized();
              setCheckingDevice(false);
            }
            return;
          }

          const posUid = auth?.linked_pos_device_uid || null;
          if (!posUid) throw new Error("Missing POS link (refresh)");

          saveDeviceAuth({ ...auth, device_type: DEVICE_TYPE });

          if (isEffectActive) {
            setPaired(true);
            setPosDeviceUid(posUid);
            setCheckingDevice(false);
          }
        } catch (error) {
          if (isEffectActive) {
            handleApiError(error, t);
            onUnauthorized();
            setCheckingDevice(false);
          }
        }
      }
    };

    verify();

    return () => {
      isEffectActive = false;
    };
  }, [onUnauthorized, t]);

  const handlePair = async () => {
    const code = pairCode.trim();
    if (!/^\d{6}$/.test(code)) {
      showAppError("Enter valid 6-digit code • أدخل رمز صحيح من 6 أرقام");
      return;
    }

    try {
      setPairingLoading(true);

      const fingerprint = await generateFingerprint();

      const res = await bindDevice({
        pairing_code: code,
        device_fingerprint: fingerprint,
      });

      const ok = res?.data?.success === true;
      const auth = res?.data?.data;

      if (res?.data?.success === false) {
        handleApiError(res, t);
        return;
      }

      if (!ok || !auth?.token) {
        showAppError("Invalid/expired code • الرمز غير صالح أو منتهي");
        return;
      }

      if (auth?.device_type && auth.device_type !== DEVICE_TYPE) {
        showAppError(
          "This code is not for Customer Display (CDS) • هذا الرمز ليس لشاشة العميل",
        );
        return;
      }

      const posUid = auth?.linked_pos_device_uid || null;
      if (!posUid) {
        showAppError("POS link missing (linked_pos_device_uid)");
        return;
      }

      saveDeviceAuth({ ...auth, device_type: DEVICE_TYPE });

      message.success("Paired successfully • تم الربط بنجاح");

      resetEcho();
      subscribedChannelRef.current = null;

      setPaired(true);
      setPosDeviceUid(posUid);
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setPairingLoading(false);
    }
  };

  useEffect(() => {
    if (!paired || !posDeviceUid) return;

    const token = getDeviceToken(DEVICE_TYPE);
    if (!token) return;

    getEcho();
    if (!window.Echo || typeof window.Echo.private !== "function") return;

    setEchoAuthToken(token);

    const channelName = `terminal.${posDeviceUid}.cds`;

    if (subscribedChannelRef.current === channelName) return;

    try {
      const prev = subscribedChannelRef.current;
      if (prev) window.Echo.leave(prev);
    } catch {}

    subscribedChannelRef.current = channelName;

    const cdsChannel = window.Echo.private(channelName);

    cdsChannel.listenForWhisper("cart-update", (payload) => {
      const items = Array.isArray(payload?.items) ? payload.items : [];

      setData((prev) => ({
        ...prev,
        status: payload?.status || (items.length ? "ACTIVE" : "IDLE"),
        items,
        totalAmount: toNumber(payload?.total),
        totalTax: toNumber(payload?.totalTax),
        shop: prev.shop,
        cashier_name: prev.cashier_name,
      }));
    });

    cdsChannel.listenForWhisper("active-pos-user", (payload) => {
      const name = payload?.name || "";
      if (name) {
        setData((prev) => ({
          ...prev,
          cashier_name: name,
        }));
      }
    });

    cdsChannel.listen(".device-unbound", (evt) => {
      if (evt?.device_type && evt.device_type !== DEVICE_TYPE) return;

      const myUid = getDeviceAuth(DEVICE_TYPE)?.device_uid;

      if (!evt?.device_uid) {
        const isBroadcast =
          evt?.unbind_all === true ||
          evt?.scope === "branch" ||
          evt?.all === true;
        if (isBroadcast) {
          onUnauthorized();
        }
        return;
      }

      if (evt.device_uid === myUid) {
        onUnauthorized();
      }
    });

    return () => {
      try {
        window.Echo.leave(channelName);
      } catch {}

      if (subscribedChannelRef.current === channelName) {
        subscribedChannelRef.current = null;
      }
    };
  }, [paired, posDeviceUid, onUnauthorized]);

  useEffect(() => {
    if (!data.items?.length) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data.items]);

  const rtl = true;

  const getProductNameAr = (it) => it?.name_ar || it?.nameAr || it?.name || "";
  const getProductNameEn = (it) => it?.name_en || it?.nameEn || it?.name || "";

  const getSelectedTagPairs = (it) => {
    const allTags = Array.isArray(it?.tags) ? it.tags : [];
    const selectedIdsRaw = Array.isArray(it?.selectedTagIds)
      ? it.selectedTagIds
      : [];

    if (!selectedIdsRaw.length) return [];

    const selectedIds = new Set(selectedIdsRaw.map((x) => Number(x)));

    return allTags
      .filter((t) => selectedIds.has(Number(t?.id)))
      .map((t) => ({
        ar: t?.name_ar || "",
        en: t?.name || "",
      }))
      .filter((p) => p.ar || p.en);
  };

  const labels = useMemo(
    () => ({
      waitingAr: "بانتظار إضافة المنتجات…",
      waitingEn: "Waiting for items…",
      qtyAr: "الكمية",
      qtyEn: "Qty",
      optionsAr: "الخيارات",
      optionsEn: "Options",
      totalAr: "الإجمالي",
      totalEn: "TOTAL",
      taxAr: "الضريبة",
      taxEn: "Tax",
      reviewAr: "يرجى المراجعة قبل الدفع",
      reviewEn: "Please review before paying",
      thanksAr: "شكرًا لك!",
      thanksEn: "Thank you!",
      poweredBy: "Powered by anypos.ai",
      salesAr: "البائع",
      salesEn: "Sales Person",
      itemsAr: "المنتجات",
      itemsEn: "Order Items",
      activeAr: "طلب نشط",
      activeEn: "Active Order",
      idleAr: "جاهز للاستقبال",
      idleEn: "Ready",
    }),
    [],
  );

  const hasItems = data.items?.length > 0;
  const itemCount = data.items?.reduce((sum, item) => {
    return sum + (Number(item?.quantity ?? item?.qty ?? 0) || 0);
  }, 0);

  const pairingBox = (
    <div style={customerDisplayStyles.pairingPage}>
      <Card
        style={customerDisplayStyles.pairingCard}
        title={
          <div style={customerDisplayStyles.pairingHeader}>
            <div style={customerDisplayStyles.pairingIcon}>CDS</div>
            <div style={customerDisplayStyles.pairingTitle}>
              Pair Customer Display
            </div>
            <div style={customerDisplayStyles.pairingSubTitle}>
              ربط شاشة العميل
            </div>
          </div>
        }
      >
        <Input
          value={pairCode}
          onChange={(e) => setPairCode(e.target.value)}
          placeholder="123456"
          maxLength={6}
          inputMode="numeric"
          style={customerDisplayStyles.pairingInput}
        />

        <Button
          type="primary"
          block
          loading={pairingLoading}
          onClick={handlePair}
          style={customerDisplayStyles.pairingButton}
        >
          Pair • ربط
        </Button>
      </Card>
    </div>
  );

  if (checkingDevice) {
    return (
      <div style={customerDisplayStyles.loadingPage}>
        <Spin size="large" />
      </div>
    );
  }

  if (!paired) return pairingBox;

  return (
    <div dir={rtl ? "rtl" : "ltr"} style={customerDisplayStyles.page}>
      <section style={customerDisplayStyles.itemsPanel}>
        <div style={customerDisplayStyles.itemsHeader}>
          <div>
            <div style={customerDisplayStyles.eyebrow}>
              {labels.itemsAr} / {labels.itemsEn}
            </div>
            <div style={customerDisplayStyles.panelTitle}>
              {hasItems ? `${itemCount} ${labels.qtyAr}` : labels.waitingAr}
            </div>
          </div>

          <div style={customerDisplayStyles.statusPill}>
            <span
              style={{
                ...customerDisplayStyles.statusDot,
                ...(hasItems
                  ? customerDisplayStyles.statusDotActive
                  : customerDisplayStyles.statusDotIdle),
              }}
            />
            {hasItems
              ? `${labels.activeAr} / ${labels.activeEn}`
              : `${labels.idleAr} / ${labels.idleEn}`}
          </div>
        </div>

        <div style={customerDisplayStyles.scrollArea}>
          {hasItems ? (
            data.items.map((it, idx) => {
              const selectedTagPairs = getSelectedTagPairs(it);
              const qty = Number(it?.quantity ?? it?.qty ?? 0) || 0;
              const price = toNumber(it?.price);

              return (
                <div
                  key={
                    it?.cart_key ||
                    it?.id ||
                    `${it?.name_en || it?.name}-${idx}`
                  }
                  style={customerDisplayStyles.itemCard}
                >
                  <div style={customerDisplayStyles.itemRow}>
                    <div style={customerDisplayStyles.productInfo}>
                      <div style={customerDisplayStyles.productNameAr}>
                        {getProductNameAr(it) || getProductNameEn(it)}
                      </div>

                      <div style={customerDisplayStyles.productNameEn}>
                        {getProductNameEn(it)}
                      </div>

                      {selectedTagPairs.length > 0 && (
                        <div style={{ marginTop: 11 }}>
                          <div style={customerDisplayStyles.optionsLabel}>
                            {labels.optionsAr} / {labels.optionsEn}
                          </div>

                          <div style={customerDisplayStyles.tagWrap}>
                            {selectedTagPairs.map((op, tIdx) => {
                              const text =
                                op.ar && op.en
                                  ? `${op.ar} / ${op.en}`
                                  : op.ar || op.en;

                              return (
                                <span
                                  key={`${it?.cart_key || it?.id || idx}-tag-${tIdx}`}
                                  style={customerDisplayStyles.tagPill}
                                >
                                  {text}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div style={customerDisplayStyles.qtyLine}>
                        {labels.qtyAr} / {labels.qtyEn}: {qty} ×{" "}
                        <CurrencyFormatter value={price} />
                      </div>
                    </div>

                    <div style={customerDisplayStyles.itemPrice}>
                      <CurrencyFormatter value={price * qty} />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={customerDisplayStyles.emptyState}>
              <div style={customerDisplayStyles.emptyIcon}>+</div>
              <div style={customerDisplayStyles.emptyTitle}>
                {labels.waitingAr}
              </div>
              <div style={customerDisplayStyles.emptySubTitle}>
                {labels.waitingEn}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </section>

      <aside style={customerDisplayStyles.summaryPanel}>
        <div style={customerDisplayStyles.brandCard}>
          {data.shop?.logo ? (
            <img
              src={data.shop.logo}
              alt="Logo"
              style={customerDisplayStyles.logo}
            />
          ) : (
            <div style={customerDisplayStyles.logoFallback}>POS</div>
          )}

          <div style={customerDisplayStyles.brandInfo}>
            <div style={customerDisplayStyles.storeName}>
              {data.shop?.business_name || "Store"}
            </div>

            <div style={customerDisplayStyles.cashier}>
              {labels.salesAr} / {labels.salesEn}: {data.cashier_name || "—"}
            </div>

            <div style={customerDisplayStyles.review}>
              {labels.reviewAr} — {labels.reviewEn}
            </div>
          </div>
        </div>

        <div style={customerDisplayStyles.totalCard}>
          <div style={customerDisplayStyles.totalLabel}>
            {labels.totalAr} / {labels.totalEn}
          </div>

          <div style={customerDisplayStyles.totalValue}>
            <CurrencyFormatter value={data.totalAmount} />
          </div>

          <div style={customerDisplayStyles.taxBox}>
            <span>
              {labels.taxAr} / {labels.taxEn}
            </span>
            <strong>
              <CurrencyFormatter value={data.totalTax} />
            </strong>
          </div>
        </div>

        <div style={customerDisplayStyles.footer}>
          <div>
            {labels.thanksAr} — {labels.thanksEn}
          </div>

          <div style={customerDisplayStyles.powered}>{labels.poweredBy}</div>
        </div>
      </aside>
    </div>
  );
};

export default CustomerExternalDisplay;
